import { useMutation } from '@tanstack/react-query'
import { Check, Copy, TriangleAlert } from 'lucide-react'
import { useRef, useState } from 'react'

import { CoverPicker } from './CoverPicker'
import { FLOWS, flowById, type FlowId } from './flows'
import type { CreateShareLinkBody, CreatedShareLink } from '../../api/types.gen'
import { WELCOME_MAX, shareUrl } from './types'
import { ApiException, api } from '../../api/client'
import { Banner, Button, Dialog, Field, ICON_STROKE, Textarea, cx } from '../../ui'

/**
 * Se monta al abrir y se desmonta al cerrar, por eso no recibe `open`.
 *
 * Es lo que garantiza que empiece limpio: reiniciar el formulario desde un
 * efecto cuando `open` pasa a false provoca renders en cascada —lo señala el
 * linter de hooks, y con razón—, y olvidarse de reiniciar un campo dejaría el
 * enlace anterior en pantalla, que además ya no se puede volver a mostrar.
 */
interface Props {
  projectId: string
  onClose: () => void
  onCreated: () => void
}

/** Los plazos que un estudio usa de verdad. Nadie escribe una fecha a mano. */
const EXPIRIES = [
  { days: null, label: 'Sin caducidad' },
  { days: 30, label: '30 días' },
  { days: 90, label: '3 meses' },
  { days: 365, label: '1 año' },
] as const

export function CreateShareLinkDialog({ projectId, onClose, onCreated }: Props) {
  const [flow, setFlow] = useState<FlowId>('entrega')
  const [label, setLabel] = useState('')
  const [quota, setQuota] = useState('20')
  /*
   | El cupo de RECEPCIÓN, en megas.
   |
   | Es lo que impide que quien reenvíe la dirección le gaste el plan entero al
   | estudio: sin tope, un enlace de recepción repartido por WhatsApp autoriza a
   | subir hasta agotar la cuota de la cuenta. 500 MB cubre de sobra el caso
   | normal —un puñado de fotos de referencia y un logo— sin dejar la puerta
   | abierta de par en par.
   */
  const [uploadMb, setUploadMb] = useState('500')
  const [password, setPassword] = useState('')
  const [days, setDays] = useState<number | null>(null)
  const [coverAssetId, setCoverAssetId] = useState<string | null>(null)
  const [welcome, setWelcome] = useState('')
  const [created, setCreated] = useState<CreatedShareLink | null>(null)
  const [failure, setFailure] = useState<string | null>(null)

  const selected = flowById(flow)

  const create = useMutation({
    mutationFn: () => {
      const expiresAt =
        days === null ? null : new Date(Date.now() + days * 86_400_000).toISOString()

      return api.post<{ data: CreatedShareLink }, CreateShareLinkBody>(`/projects/${projectId}/share-links`, {
        label: label.trim() === '' ? null : label.trim(),
        permissions: selected.permissions,
        download_scope: selected.downloadScope,
        max_selections: selected.needsQuota ? Number(quota) : null,
        // La API lo quiere en bytes y con un mínimo de 1 MiB.
        max_upload_bytes: selected.receives === true ? Number(uploadMb) * 1024 * 1024 : null,
        password: password.trim() === '' ? null : password,
        expires_at: expiresAt,
        cover_asset_id: coverAssetId,
        welcome_message: welcome.trim() === '' ? null : welcome.trim(),
      })
    },
    onSuccess: (result) => {
      setFailure(null)
      setCreated(result.data)
      onCreated()
    },
    onError: (error) => {
      setFailure(
        error instanceof ApiException
          ? error.message
          : 'No se pudo crear el enlace. Revisa tu conexión.',
      )
    },
  })

  const quotaInvalid = selected.needsQuota && (Number(quota) < 1 || !Number.isInteger(Number(quota)))

  // El mínimo de 1 MiB es el mismo que valida la API: por debajo se crea un
  // enlace de recepción que no acepta ni un archivo.
  const uploadInvalid =
    selected.receives === true && (Number(uploadMb) < 1 || !Number.isInteger(Number(uploadMb)))

  return (
    <Dialog
      open
      onClose={onClose}
      title={created === null ? 'Nuevo enlace de entrega' : 'Tu enlace está listo'}
      description={
        created === null
          ? 'Tu cliente no necesita crear ninguna cuenta: abre el enlace y ya está dentro.'
          : undefined
      }
      footer={
        created === null ? (
          <>
            <Button variant="ghost" onClick={onClose}>
              Cancelar
            </Button>
            <Button
              disabled={quotaInvalid || uploadInvalid}
              busy={create.isPending && 'Creando…'}
              onClick={() => {
                setFailure(null)
                create.mutate()
              }}
            >
              Crear enlace
            </Button>
          </>
        ) : (
          <Button onClick={onClose}>Ya lo he copiado</Button>
        )
      }
    >
      {created !== null ? <Result link={created} /> : (
        <div className="flex flex-col gap-6">
          {/* 1 · Qué tipo de entrega */}
          <fieldset className="border-0 p-0">
            <legend className="text-label font-semibold text-ink">¿Qué va a poder hacer?</legend>

            <div className="mt-3 flex flex-col gap-2">
              {FLOWS.map((option) => (
                <label key={option.id} className="block cursor-pointer">
                  <input
                    type="radio"
                    name="flow"
                    value={option.id}
                    checked={flow === option.id}
                    onChange={() => {
                      setFlow(option.id)

                      /*
                       | Un enlace que recibe TIENE que caducar, y lo impone el
                       | servidor (y un CHECK en la base). Sin esto, elegir
                       | recepción y dejar «Sin caducidad» —que es el valor por
                       | defecto— produciría un 422 al pulsar Crear, después de
                       | rellenar todo el formulario.
                       |
                       | 30 días es lo que se tarda en mandar un material de
                       | partida. La puerta se cierra sola.
                       */
                      if (option.receives === true && days === null) {
                        setDays(30)
                      }
                    }}
                    className="peer sr-only"
                  />
                  <span
                    className={cx(
                      'flex gap-3 rounded-card border p-3 transition-colors',
                      'peer-focus-visible:ring-2 peer-focus-visible:ring-accent',
                      flow === option.id
                        ? 'border-accent bg-accent-subtle'
                        : 'border-line hover:border-line-strong',
                    )}
                  >
                    <option.icon
                      size={18}
                      strokeWidth={ICON_STROKE}
                      aria-hidden
                      className={cx('mt-0.5 shrink-0', flow === option.id ? 'text-accent' : 'text-ink-subtle')}
                    />
                    <span className="min-w-0">
                      <span className="block text-label font-medium text-ink">{option.label}</span>
                      <span className="mt-0.5 block text-body text-ink-muted">{option.summary}</span>
                      <span className="mt-1 block text-caption text-ink-subtle">{option.when}</span>
                    </span>
                  </span>
                </label>
              ))}
            </div>
          </fieldset>

          {/* 2 · El cupo, sólo donde significa algo */}
          {selected.needsQuota && (
            <Field
              label="¿Cuántas fotos puede elegir?"
              type="number"
              min={1}
              value={quota}
              onChange={(event) => setQuota(event.target.value)}
              hint="Las que le vendiste. La galería deja de aceptar más al llegar a este número."
              error={quotaInvalid ? 'Tiene que ser un número de 1 en adelante.' : null}
            />
          )}

          {selected.receives === true && (
            <Field
              label="¿Cuánto puede mandarte, como mucho?"
              type="number"
              min={1}
              value={uploadMb}
              onChange={(event) => setUploadMb(event.target.value)}
              hint="En megas, y para todo el enlace. Quien tenga la dirección puede subir hasta ese tope, así que conviene ajustarlo a lo que le pediste."
              error={uploadInvalid ? 'Tiene que ser un número de 1 en adelante.' : null}
            />
          )}

          {/* 3 · Para quién */}
          <Field
            label="¿Para quién es? (opcional)"
            value={label}
            onChange={(event) => setLabel(event.target.value)}
            placeholder="Los novios"
            hint="Sólo lo ves tú. Sirve para distinguir varios enlaces del mismo proyecto."
          />

          {/*
            4 · Cómo abre la galería

            Portada y saludo van juntos y en este orden porque son lo que el
            cliente ve antes que nada, y son de ESTE enlace: sobre la misma
            boda, los novios y los padres de la novia abren con otra foto y con
            otro texto. Justo debajo de «¿para quién es?», que es donde el
            estudio acaba de pensar en esa persona.
          */}
          <div className="flex flex-col gap-4 rounded-card bg-surface-sunken p-3">
            {/*
              La portada sólo donde hay galería que abrir.
              En un enlace de recepción no hay ninguna —y el servidor ya no la
              firma sin `view`—, así que ofrecerla es pedirle al estudio que
              elija una foto que su cliente no va a ver.
            */}
            {selected.receives !== true && (
              <CoverPicker projectId={projectId} value={coverAssetId} onChange={setCoverAssetId} />
            )}

            <Textarea
              label="Un saludo (opcional)"
              rows={2}
              value={welcome}
              maxLength={WELCOME_MAX}
              onChange={(event) => setWelcome(event.target.value)}
              placeholder={
                selected.receives === true
                  ? 'Mándanos las fotos de la finca y el logo del catering, por favor.'
                  : 'Gracias por dejarnos contar vuestro día. Aquí están vuestras fotos.'
              }
              hint={
                selected.receives === true
                  ? 'Sale sobre el botón de enviar. Es donde se le dice qué tiene que mandarte.'
                  : 'Sale bajo el título de la galería. Lo lee tu cliente, no tú.'
              }
            />
          </div>

          {/* 5 · Caducidad */}
          <div className="flex flex-col gap-2">
            <span className="text-label font-medium text-ink">¿Cuánto dura?</span>
            <div className="flex flex-wrap gap-2">
              {EXPIRIES.filter(
                (option) => option.days !== null || selected.receives !== true,
              ).map((option) => (
                <button
                  key={option.label}
                  type="button"
                  onClick={() => setDays(option.days)}
                  aria-pressed={days === option.days}
                  className={cx(
                    'rounded-full border px-3 py-1.5 text-label transition-colors',
                    days === option.days
                      ? 'border-accent bg-accent-subtle font-medium text-accent'
                      : 'border-line text-ink-muted hover:border-line-strong hover:text-ink',
                  )}
                >
                  {option.label}
                </button>
              ))}
            </div>
            <p className="text-caption text-ink-subtle">
              {selected.receives === true
                ? 'Un enlace que recibe archivos tiene que caducar: es una puerta abierta a tu almacenamiento, y una que nadie recuerda haber dejado abierta no se cierra nunca.'
                : 'Sin caducidad por defecto: si dentro de dos años tu cliente quiere volver a bajar sus fotos, seguirán ahí.'}
            </p>
          </div>

          {/* 6 · Contraseña */}
          <Field
            label="Contraseña (opcional)"
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            autoComplete="off"
            hint="Sólo si el material es delicado. Tendrás que dársela por otro medio."
          />

          {failure !== null && (
            <Banner tone="danger" title="No se pudo crear" live="alert">
              {failure}
            </Banner>
          )}
        </div>
      )}
    </Dialog>
  )
}

/**
 * Copiar sin dar por hecho que hay portapapeles.
 *
 * `navigator.clipboard` SÓLO existe en contexto seguro: HTTPS, `localhost` o
 * `*.localhost`. En desarrollo se entra por `http://acme.<ip>.nip.io:5173` —el
 * dominio que permite probar desde el móvil— y ahí es `undefined`, así que
 * `navigator.clipboard.writeText()` lanza antes de copiar nada. `lvh.me` tampoco
 * sirve aunque resuelva a 127.0.0.1: el navegador mira el nombre, no la IP.
 *
 * Antes eso caía en un `catch` vacío. El botón se quedaba quieto, sin copiar y
 * sin decirlo, justo en la pantalla donde el valor no se puede recuperar.
 *
 * El respaldo es `execCommand('copy')`, que está obsoleto y sigue siendo lo
 * único que funciona sin contexto seguro. En producción no se llega a usar.
 */
async function alPortapapeles(texto: string): Promise<boolean> {
  if (window.isSecureContext && navigator.clipboard !== undefined) {
    try {
      await navigator.clipboard.writeText(texto)

      return true
    } catch {
      // Puede fallar igualmente si el permiso está denegado. Sigue el respaldo.
    }
  }

  const provisional = document.createElement('textarea')

  provisional.value = texto
  provisional.setAttribute('readonly', '')
  // Fuera de la vista, pero NO con `display:none`: lo que no se pinta no se
  // puede seleccionar, y sin selección no hay nada que copiar.
  provisional.style.position = 'fixed'
  provisional.style.top = '-9999px'
  document.body.appendChild(provisional)

  try {
    provisional.select()

    return document.execCommand('copy')
  } catch {
    return false
  } finally {
    provisional.remove()
  }
}

/**
 * El enlace recién creado.
 *
 * Es el único momento en que existe. En la base sólo queda su sha256, así que
 * cerrar sin copiar significa revocarlo y crear otro. Eso hay que decirlo aquí
 * y con estas palabras, no en una ayuda al pie.
 */
function Result({ link }: { link: CreatedShareLink }) {
  const [copia, setCopia] = useState<'listo' | 'hecha' | 'fallida'>('listo')
  const campo = useRef<HTMLInputElement>(null)
  const url = shareUrl(link.token)

  const copy = async (): Promise<void> => {
    const hecha = await alPortapapeles(url)

    setCopia(hecha ? 'hecha' : 'fallida')

    if (hecha) {
      setTimeout(() => setCopia('listo'), 2000)

      return
    }

    // Si no se pudo, al menos se deja seleccionado: así queda a un Ctrl+C.
    campo.current?.focus()
    campo.current?.select()
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <label htmlFor="share-url" className="text-label font-medium text-ink">
          El enlace para tu cliente
        </label>

        <div className="flex gap-2">
          <input
            id="share-url"
            ref={campo}
            readOnly
            value={url}
            onFocus={(event) => event.target.select()}
            className="min-w-0 flex-1 rounded-control border border-line-strong bg-surface px-3 py-2.5 text-label text-ink"
          />
          <Button variant="secondary" onClick={() => void copy()} className="shrink-0">
            {copia === 'hecha' ? (
              <Check size={16} strokeWidth={2.5} aria-hidden className="text-positive" />
            ) : (
              <Copy size={16} strokeWidth={ICON_STROKE} aria-hidden />
            )}
            {copia === 'hecha' ? 'Copiado' : 'Copiar'}
          </Button>
        </div>

        {/*
          Que falle se DICE. Esta pantalla enseña un enlace que no se puede
          volver a ver: un botón que no hace nada y encima calla es la peor
          forma posible de perderlo.
        */}
        {copia === 'fallida' && (
          <p role="alert" className="text-caption text-danger">
            Tu navegador no nos deja usar el portapapeles aquí. El enlace ya está
            seleccionado: cópialo con Ctrl+C (o Cmd+C).
          </p>
        )}
      </div>

      <Banner tone="warning" title="Cópialo ahora">
        <p>
          No podemos volver a enseñarte esta dirección: sólo guardamos una huella
          suya, para que ni siquiera nosotros podamos entrar a la galería de tu
          cliente. Si la pierdes, anula este enlace y crea otro.
        </p>
      </Banner>

      {link.requires_password && (
        <p className="flex items-start gap-2 text-caption text-ink-subtle">
          <TriangleAlert size={14} strokeWidth={ICON_STROKE} aria-hidden className="mt-0.5 shrink-0" />
          <span>
            Lleva contraseña. Mándasela por otro medio distinto del enlace — si van en el mismo
            mensaje, no protege de nada.
          </span>
        </p>
      )}
    </div>
  )
}
